/**
 * The queue: every legajo, ordered by who needs the analyst first.
 *
 * Interrupted runs lead, because nobody else will notice them; then unfavourable and insufficient,
 * which are decisions waiting to be made; favourable ones last. A legajo somebody has already
 * decided drops out of "requieren atención" however alarming its findings were — it is finished
 * work, and a queue that keeps showing finished work stops being read.
 */

import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../api/client";
import { saveFile } from "../lib/download";
import { type SavedView, loadViews, removeView, saveView } from "../lib/views";
import {
  QUEUE_VIEWS,
  type QueueView,
  byGradeDescending,
  queueKey,
  queueRows,
  timeAgo,
  viewParam,
} from "../lib/model";
import {
  Button,
  Chip,
  Dot,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Spinner,
  Table,
  Td,
  Th,
} from "../components/ui";

type SortKey = "prioridad" | "calificacion" | "empresa" | "actualizado";

// Cincuenta filas es lo que entra en una pantalla sin que nadie haga scroll infinito.
const PAGE_SIZE = 50;

export default function Portfolio() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("prioridad");
  const [page, setPage] = useState(1);
  // Las filas se recorren con flechas, como se lee la tabla. Cada una se registra acá para poder
  // moverle el foco sin consultar el DOM por selector.
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  const view = (QUEUE_VIEWS.find((v) => v === params.get("vista")) ?? "Todos") as QueueView;
  const archived = params.get("archivados") === "1";
  const grade = params.get("calificacion");
  const assignedTo = params.get("a_cargo");
  const decidedParam = params.get("decision");
  const decided =
    decidedParam === "si" ? true
    : decidedParam === "no" ? false
    : undefined;

  const filters = {
    archived,
    q: query,
    view: viewParam(view),
    grade,
    assignedTo,
    decided,
  };
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [views, setViews] = useState<SavedView[]>(() => loadViews());
  const queryClient = useQueryClient();
  const users = useQuery({
    queryKey: ["users"],
    queryFn: api.listUsers,
    networkMode: "always",
  });

  // La búsqueda y la paginación pasan en el servidor. El orden y las vistas se aplican sobre la
  // página que vuelve: son decisiones de presentación, y ordenar cincuenta filas es gratis.
  const cases = useQuery({
    queryKey: ["cases", filters, page],
    queryFn: () => api.listCases({ ...filters, page, size: PAGE_SIZE }),
    refetchInterval: 5000,
    placeholderData: (previous) => previous,
  });

  const total = cases.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const rows = useMemo(() => {
    // El filtrado pasa en la base; acá sólo queda el orden, que es presentación y cuesta nada
    // sobre cincuenta filas.
    const filtered = queueRows(cases.data?.items ?? []);
    if (sort === "calificacion") {
      return byGradeDescending(filtered);
    }
    if (sort === "empresa") {
      return [...filtered].sort((a, b) => a.legalName.localeCompare(b.legalName));
    }
    if (sort === "actualizado") {
      return [...filtered].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
    }
    return filtered; // already ordered by who needs attention first
  }, [cases.data, sort]);

  const bulk = useMutation({
    mutationFn: ({
      action,
      userId,
    }: {
      action: "assign" | "archive" | "restore";
      userId?: string;
    }) => api.bulk([...selected], action, userId ?? null),
    onSuccess: async () => {
      setSelected(new Set());
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });

  async function exportCsv() {
    saveFile(await api.exportCases(filters), "cartera.csv");
  }

  /** Un nombre propuesto para la vista, armado con lo que el filtro ya dice. */
  function describeFilters(): string {
    const parts = [view !== "Todos" ? view : null, grade ? `Calificación ${grade}` : null];
    return parts.filter(Boolean).join(" · ") || "Toda la cartera";
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setParam(key: string, value: string | null) {
    setPage(1);
    setSelected(new Set());
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  if (cases.isError) return <ErrorBanner {...friendlyError(cases.error)} />;
  if (!cases.data) return <Spinner label="Cargando la cartera…" />;

  return (
    <>
      <PageHeader
        title={archived ? "Legajos archivados" : "Cartera de legajos"}
        subtitle={
          archived ?
            "Fuera de la cartera activa. Se restauran desde el propio legajo."
          : "Ordenados por los que requieren tu atención primero."
        }
        actions={
          <button
            onClick={() => setParam("archivados", archived ? null : "1")}
            className="text-sm text-primary underline underline-offset-2"
          >
            {archived ? "Ver la cartera activa" : "Ver archivados"}
          </button>
        }
      />
      <div className="my-5 border-b border-line" />

      {total === 0 && !query ?
        <EmptyState
          title={archived ? "No hay legajos archivados" : "Todavía no hay legajos"}
          text={
            archived ?
              "Cuando archives uno, va a aparecer acá."
            : "Creá el primero desde «Nuevo legajo» y subí los estados contables, el informe de bureau y el memo."
          }
        />
      : <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap rounded-lg border border-line p-0.5" role="tablist">
              {QUEUE_VIEWS.map((option) => (
                <button
                  key={option}
                  role="tab"
                  aria-selected={view === option}
                  onClick={() => setParam("vista", option === "Todos" ? null : option)}
                  className={`rounded-md px-3 py-1.5 text-sm ${
                    view === option ? "bg-canvas font-medium" : "text-muted hover:text-ink"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por empresa o CUIT"
              aria-label="Buscar por empresa o CUIT"
              className="min-w-56 flex-1 rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
            />
            <label className="flex items-center gap-2 text-sm text-muted">
              Ordenar
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as SortKey)}
                className="rounded-lg border border-line px-2 py-1.5 text-sm text-ink"
              >
                <option value="prioridad">Por prioridad</option>
                <option value="calificacion">Por calificación</option>
                <option value="empresa">Por empresa</option>
                <option value="actualizado">Por actividad reciente</option>
              </select>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-muted">
              Calificación
              <select
                value={grade ?? ""}
                onChange={(e) => setParam("calificacion", e.target.value || null)}
                className="rounded-lg border border-line px-2 py-1.5 text-sm text-ink"
              >
                <option value="">Todas</option>
                {["A", "B", "C", "D", "E"].map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-muted">
              A cargo
              <select
                value={assignedTo ?? ""}
                onChange={(e) => setParam("a_cargo", e.target.value || null)}
                className="rounded-lg border border-line px-2 py-1.5 text-sm text-ink"
              >
                <option value="">Cualquiera</option>
                {(users.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-muted">
              Decisión
              <select
                value={decidedParam ?? ""}
                onChange={(e) => setParam("decision", e.target.value || null)}
                className="rounded-lg border border-line px-2 py-1.5 text-sm text-ink"
              >
                <option value="">Cualquiera</option>
                <option value="no">Sin decidir</option>
                <option value="si">Decididos</option>
              </select>
            </label>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  const name = window.prompt("¿Cómo se llama esta vista?", describeFilters());
                  if (name) setViews(saveView(name, Object.fromEntries(params)));
                }}
              >
                Guardar esta vista
              </Button>
              {/* La planilla es como se arma el comité del lunes: baja lo que el filtro dice. */}
              <Button onClick={exportCsv}>Exportar a planilla</Button>
            </div>
          </div>

          {views.length > 0 ?
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted">Mis vistas:</span>
              {views.map((saved) => (
                <span
                  key={saved.name}
                  className="flex items-center gap-1 rounded-full border border-line py-1 pr-1 pl-3"
                >
                  <button
                    onClick={() => {
                      setPage(1);
                      setSelected(new Set());
                      setParams(new URLSearchParams(saved.params), {
                        replace: true,
                      });
                    }}
                    className="text-primary"
                  >
                    {saved.name}
                  </button>
                  <button
                    onClick={() => setViews(removeView(saved.name))}
                    aria-label={`Olvidar la vista ${saved.name}`}
                    className="rounded-full px-1.5 text-muted hover:text-ink"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          : null}

          {selected.size > 0 ?
            <div
              role="status"
              aria-live="polite"
              className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-canvas px-4 py-2.5 text-sm"
            >
              <span className="font-medium">{selected.size} legajo(s) seleccionado(s)</span>
              <label className="flex items-center gap-2 text-muted">
                Asignar a
                <select
                  defaultValue=""
                  onChange={(e) =>
                    e.target.value && bulk.mutate({ action: "assign", userId: e.target.value })
                  }
                  className="rounded-lg border border-line px-2 py-1.5 text-sm text-ink"
                >
                  <option value="">Elegir…</option>
                  {(users.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>
              <Button
                variant="ghost"
                onClick={() => bulk.mutate({ action: archived ? "restore" : "archive" })}
              >
                {archived ? "Restaurar" : "Archivar"}
              </Button>
              <button
                onClick={() => setSelected(new Set())}
                className="text-muted underline underline-offset-2"
              >
                Limpiar selección
              </button>
              {bulk.isError ?
                <span className="text-bad">No se pudo aplicar.</span>
              : null}
            </div>
          : null}

          <p className="mt-3 text-sm text-muted" role="status" aria-live="polite">
            {rows.length} de {total} legajo(s)
            {pages > 1 ? ` · página ${page} de ${pages}` : ""}
          </p>

          <div className="mt-3">
            {rows.length === 0 ?
              <EmptyState
                title="Ningún legajo coincide"
                text="Probá con otro filtro o limpiá la búsqueda."
              />
            : <Table>
                <thead>
                  <tr>
                    <Th>
                      <input
                        type="checkbox"
                        aria-label="Seleccionar toda la página"
                        checked={rows.length > 0 && selected.size === rows.length}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                        }
                      />
                    </Th>
                    <Th>Empresa</Th>
                    <Th>Estado</Th>
                    <Th>Calificación</Th>
                    <Th>Riesgo sugerido</Th>
                    <Th>Decisión</Th>
                    <Th>A cargo</Th>
                    <Th right>Alertas</Th>
                    <Th right>Actividad</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="link"
                      aria-label={`Abrir el legajo de ${row.legalName}`}
                      onClick={() => navigate(`/legajos/${row.id}`)}
                      ref={(el) => {
                        rowRefs.current[index] = el;
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(`/legajos/${row.id}`);
                          return;
                        }
                        const target = queueKey(e.key, index, rows.length);
                        if (target !== null) {
                          e.preventDefault();
                          rowRefs.current[target]?.focus();
                        }
                      }}
                      className="cursor-pointer hover:bg-canvas"
                    >
                      <Td>
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${row.legalName}`}
                          checked={selected.has(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggle(row.id)}
                        />
                      </Td>
                      <Td>
                        <span className="block font-medium">{row.legalName}</span>
                        <span className="text-xs tabular-nums text-muted">{row.cuit}</span>
                      </Td>
                      <Td>
                        <span className="flex items-center gap-2">
                          <Dot tone={row.state.tone} />
                          {row.state.text}
                        </span>
                      </Td>
                      <Td>
                        {row.grade ?
                          <span className="flex items-center gap-1.5">
                            <Chip text={row.grade.text} tone={row.grade.tone} />
                            {row.gradeIsPartial ?
                              <span
                                className="text-xs text-warn"
                                title="Se calculó sobre un legajo incompleto"
                              >
                                parcial
                              </span>
                            : null}
                          </span>
                        : <span className="text-muted">—</span>}
                      </Td>
                      <Td>
                        {row.risk ?
                          <Chip text={row.risk.text} tone={row.risk.tone} />
                        : "—"}
                      </Td>
                      <Td>
                        {row.decision ?
                          <Chip text={row.decision.text} tone={row.decision.tone} />
                        : <span className="text-muted">{row.analysed ? "Pendiente" : "—"}</span>}
                      </Td>
                      <Td className={row.assignedTo ? "" : "text-muted"}>
                        {row.assignedTo ?? "Sin asignar"}
                      </Td>
                      <Td right className={row.redFlags ? "font-semibold text-bad" : "text-muted"}>
                        {row.redFlags || "—"}
                      </Td>
                      <Td right className="text-muted">
                        {timeAgo(row.updatedAt)}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            }
          </div>

          {pages > 1 ?
            <div className="mt-4 flex items-center justify-between">
              <Button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                ← Anteriores
              </Button>
              <span className="text-sm text-muted">
                Página {page} de {pages}
              </span>
              <Button
                disabled={page >= pages}
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
              >
                Siguientes →
              </Button>
            </div>
          : null}
        </>
      }
    </>
  );
}
